import {faFile, faTrash} from '@fortawesome/pro-regular-svg-icons';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {Button, Input, Tooltip} from 'antd';
import {useEffect, useRef, useState} from 'react';

import {useLocale} from '../../../../../../../provider/locale/locale-hook';
import {AddressCommentTemplateStateType} from '../../../../../../../service/company-v2/types/company-address-type';

import {buildAddressComment, getAddressCommentSegmentList} from './address-comment-templates-helper';
import {AddressCommentTemplatesModal} from './address-comment-templates-modal';
import {AddressCommentSegmentKindEnum, AddressCommentTemplateType} from './address-comment-templates-type';
import {getAddressCommentTemplateState} from './address-comment-templates-validation';
import * as styles from './address-comment-templates.scss';

type PropsType = {
    // Form.Item clones the control with an id its label points at.
    id?: string;
    value?: string | null;
    onChange?: (value: string) => void;
    // Reports the picked template and how much of it is filled in, for the validation of the field.
    onTemplateStateChange?: (templateState: AddressCommentTemplateStateType | null) => void;
};

export function AddressCommentInput(props: PropsType): JSX.Element {
    const {id, value, onChange, onTemplateStateChange} = props;

    const {getLocalizedString} = useLocale();

    const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
    // The picked template lives for the current editing session only: the company card stores the built string.
    const [template, setTemplate] = useState<AddressCommentTemplateType | null>(null);
    const [fieldValueMap, setFieldValueMap] = useState<Record<string, string>>({});

    // The comment the field held before the template took it over — an unfinished template must not erase it.
    const savedValueRef = useRef<string>('');
    // What this control last pushed into the form, to tell our own updates from an external reset.
    const lastEmittedValueRef = useRef<string | null>(null);

    useEffect(() => {
        if (lastEmittedValueRef.current === null || value === lastEmittedValueRef.current) {
            return;
        }

        // The form value changed from the outside (reset, programmatic update): the template row is out of sync.
        lastEmittedValueRef.current = null;
        setTemplate(null);
        setFieldValueMap({});
        onTemplateStateChange?.(null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value]);

    function emitValue(nextValue: string) {
        lastEmittedValueRef.current = nextValue;
        onChange?.(nextValue);
    }

    function applyTemplate(nextTemplate: AddressCommentTemplateType) {
        savedValueRef.current = value ?? '';

        setTemplate(nextTemplate);
        setFieldValueMap({});
        setIsModalOpen(false);
        onTemplateStateChange?.(getAddressCommentTemplateState(nextTemplate, {}));

        const comment = buildAddressComment(nextTemplate, {}, getLocalizedString);

        // A template without fields is complete right away; one with fields keeps the saved comment until filled in.
        if (comment) {
            emitValue(comment);
        }
    }

    function changeFieldValue(name: string, fieldValue: string) {
        const nextFieldValueMap = {...fieldValueMap, [name]: fieldValue};

        setFieldValueMap(nextFieldValueMap);
        onTemplateStateChange?.(getAddressCommentTemplateState(template, nextFieldValueMap));

        if (template) {
            emitValue(buildAddressComment(template, nextFieldValueMap, getLocalizedString) || savedValueRef.current);
        }
    }

    function removeTemplate() {
        setTemplate(null);
        setFieldValueMap({});
        onTemplateStateChange?.(null);
        emitValue(savedValueRef.current);
    }

    const [firstField] = template?.fieldList ?? [];

    return (
        <>
            {template ? (
                <div className={styles.AddressCommentTemplates_row}>
                    <div className={styles.AddressCommentTemplates_rowContent}>
                        {getAddressCommentSegmentList(template, getLocalizedString).map((segment, index) =>
                            segment.kind === AddressCommentSegmentKindEnum.text ? (
                                // eslint-disable-next-line react/no-array-index-key
                                <span className={styles.AddressCommentTemplates_rowText} key={index}>
                                    {segment.text}
                                </span>
                            ) : (
                                <Input
                                    className={styles.AddressCommentTemplates_rowInput}
                                    // eslint-disable-next-line no-undefined
                                    id={segment.field.name === firstField?.name ? id : undefined}
                                    key={segment.field.name}
                                    onChange={(event) => changeFieldValue(segment.field.name, event.target.value)}
                                    placeholder={getLocalizedString(segment.field.hintKey)}
                                    value={fieldValueMap[segment.field.name] ?? ''}
                                />
                            )
                        )}
                    </div>

                    <Button
                        aria-label={getLocalizedString('COMPANY_FORM__ADDRESS__COMMENT__TEMPLATES__REMOVE')}
                        icon={<FontAwesomeIcon icon={faTrash} />}
                        onClick={removeTemplate}
                        type="text"
                    />
                </div>
            ) : (
                <div className={styles.AddressCommentTemplates_inputWrapper}>
                    <Input
                        id={id}
                        onChange={(event) => emitValue(event.target.value)}
                        placeholder={getLocalizedString('COMPANY_FORM__ADDRESS__COMMENT__PLACEHOLDER')}
                        size="large"
                        value={value ?? ''}
                    />

                    <Tooltip title={getLocalizedString('COMPANY_FORM__ADDRESS__COMMENT__TEMPLATES__TOOLTIP')}>
                        <Button
                            aria-label={getLocalizedString('COMPANY_FORM__ADDRESS__COMMENT__TEMPLATES__TOOLTIP')}
                            icon={<FontAwesomeIcon icon={faFile} />}
                            onClick={() => setIsModalOpen(true)}
                            size="large"
                        />
                    </Tooltip>
                </div>
            )}

            {isModalOpen && (
                <AddressCommentTemplatesModal onApply={applyTemplate} onClose={() => setIsModalOpen(false)} />
            )}
        </>
    );
}
